/**
 * The API client.
 *
 * Every call funnels through `request`, so a failure surfaces the server's
 * `error` and `remedy` rather than "Failed to fetch". Both predecessor
 * front-ends swallowed the body and showed a generic toast, which is how a
 * 25 MB upload limit became "Something went wrong".
 */

export class ApiFailure extends Error {
  constructor(
    message: string,
    readonly remedy: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiFailure';
  }
}

/**
 * The workspace the client is acting in.
 *
 * Sent as a header on every request rather than baked into the path, so
 * switching workspace is a state change rather than a navigation — and a
 * single-workspace user never has to think about it.
 */
let activeWorkspaceId: string | null = null;

export function setActiveWorkspace(id: string | null): void {
  activeWorkspaceId = id;
  if (id === null) localStorage.removeItem('dv-workspace');
  else localStorage.setItem('dv-workspace', id);
}

export function restoreActiveWorkspace(): string | null {
  activeWorkspaceId = localStorage.getItem('dv-workspace');
  return activeWorkspaceId;
}

export function workspaceHeader(): Record<string, string> {
  return activeWorkspaceId === null ? {} : { 'x-workspace-id': activeWorkspaceId };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      // Same-origin by default, but stated so a future cross-origin deployment
      // does not silently start dropping the session cookie.
      credentials: 'same-origin',
      headers: { ...workspaceHeader(), ...(init?.headers ?? {}) },
    });
  } catch {
    throw new ApiFailure('Could not reach the server.', 'Check your connection and try again.', 0);
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string; remedy?: string };
    throw new ApiFailure(
      body.error ?? `Request failed with status ${response.status}.`,
      body.remedy ?? 'Try again.',
      response.status,
    );
  }

  return (await response.json()) as T;
}

const json = (body: unknown): RequestInit => ({
  method: 'POST',
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
});

// -----------------------------------------------------------------------------
// Shapes â€” mirrors of what the API returns. Structural rather than imported, so
// the browser bundle carries no server code.
// -----------------------------------------------------------------------------

export type ColumnType = 'numeric' | 'categorical' | 'temporal' | 'boolean' | 'empty';
export type Aggregation = 'sum' | 'avg' | 'count' | 'countDistinct' | 'min' | 'max' | 'median';
export type CalendarPeriod = 'day' | 'week' | 'month' | 'quarter' | 'year';
export type ChartKind = 'bar' | 'line' | 'area' | 'pie' | 'scatter' | 'table' | 'histogram';

export interface Column {
  name: string;
  type: ColumnType;
  missing: number;
  distinct: number;
  isIdentifier: boolean;
}

export interface DatasetSummary {
  id: string;
  name: string;
  fileName: string;
  rowCount: number;
  columnCount: number;
  createdAt: string;
}

export interface Dataset {
  id: string;
  name: string;
  fileName: string;
  rowCount: number;
  columns: Column[];
  warnings: string[];
  createdAt: string;
}

export interface Scorecard {
  id: string;
  title: string;
  value: number | string;
  formatted: string;
  format: string;
  subtitle: string;
  trend: { changePercent: number; direction: 'up' | 'down' | 'flat'; basis: string } | null;
  derivedFrom: string[];
}

export interface Evidence {
  label: string;
  value: number;
  statistic: string;
  column?: string;
}

export interface Insight {
  id: string;
  kind: string;
  severity: 'info' | 'notable' | 'warning';
  headline: string;
  detail: string;
  columns: string[];
  evidence: Evidence[];
}

export interface ChartSuggestion {
  id: string;
  chartType: ChartKind;
  title: string;
  x: string;
  y?: string;
  aggregation: Aggregation;
  rationale: string;
  score: number;
}

export interface QualityIssue {
  severity: 'info' | 'notable' | 'warning';
  column: string | null;
  message: string;
  remedy: string;
}

export interface Quality {
  completeness: number;
  totalCells: number;
  missingCells: number;
  emptyColumns: string[];
  constantColumns: string[];
  duplicateRows: number;
  issues: QualityIssue[];
}

export interface Narrative {
  summary: string;
  highlights: string[];
  recommendations: string[];
  provider: string;
  model: string | null;
  degraded: boolean;
  unverified: string[];
}

export interface Report {
  id: string;
  datasetId: string;
  title: string;
  createdAt: string;
  scorecards: Scorecard[];
  insights: Insight[];
  charts: ChartSuggestion[];
  quality: Quality;
  narrative: Narrative | null;
}

export interface Bucket {
  key: string;
  value: number;
  count: number;
}

export interface AggregateResult {
  buckets: Bucket[];
  otherBucket: Bucket | null;
  totalGroups: number;
}

export interface PivotCell {
  value: number | null;
  count: number;
}

export interface PivotTable {
  rowFields: string[];
  columnField: string | null;
  columnKeys: string[];
  rows: { keys: string[]; label: string; cells: PivotCell[]; total: PivotCell }[];
  columnTotals: PivotCell[];
  grandTotal: PivotCell;
  totalsAreAdditive: boolean;
  totalRows: number;
  totalColumns: number;
  truncatedRows: number;
  truncatedColumns: number;
}

export interface Filter {
  column: string;
  operator: string;
  value?: unknown;
}

// --- forecasting --------------------------------------------------------------

export type ForecastModelName =
  | 'decomposition'
  | 'linear'
  | 'seasonal-naive'
  | 'naive'
  | 'drift'
  | 'mean';

export interface ComponentBreakdown {
  trend: number;
  seasonal: Record<string, number>;
  holiday: number;
}

export interface FittedPoint {
  at: string;
  actual: number;
  fitted: number;
  residual: number;
  components: ComponentBreakdown;
}

export interface ProjectedPoint {
  at: string;
  value: number;
  lower: number;
  upper: number;
  components: ComponentBreakdown;
}

export interface Seasonality {
  name: string;
  periodDays: number;
  fourierOrder: number;
  source: 'detected' | 'requested';
  strength: number | null;
}

export interface Changepoint {
  at: string;
  delta: number;
  slopeBefore: number;
  slopeAfter: number;
}

export interface FitDiagnostics {
  observations: number;
  parameters: number;
  r2: number;
  mae: number;
  rmse: number;
  mape: number | null;
  residualStdError: number;
}

export interface Projection {
  model: ForecastModelName;
  history: FittedPoint[];
  points: ProjectedPoint[];
  changepoints: Changepoint[];
  seasonalities: Seasonality[];
  holidays: string[];
  diagnostics: FitDiagnostics;
  confidenceLevel: number;
  intervalMs: number;
  notes: string[];
}

export interface Backtest {
  model: ForecastModelName;
  folds: {
    trainedThrough: string;
    horizon: number;
    mae: number;
    coverage: number;
    /** The held-out points each fold predicted, for the backtest chart. */
    points: { at: string; actual: number; predicted: number; lower: number; upper: number }[];
  }[];
  mae: number;
  rmse: number;
  mape: number | null;
  coverage: number;
}

export interface ModelComparison {
  evaluated: Backtest[];
  best: ForecastModelName;
  reason: string;
}

export interface Anomaly {
  at: string;
  actual: number;
  expected: number;
  lower: number;
  upper: number;
  severity: number;
  direction: 'above' | 'below';
}

// --- connectors ---------------------------------------------------------------

export interface CredentialField {
  key: string;
  label: string;
  kind: 'text' | 'password' | 'json' | 'select';
  required: boolean;
  help: string;
  options?: { value: string; label: string }[];
}

export interface ParameterField {
  key: string;
  label: string;
  kind: 'text' | 'textarea' | 'select' | 'multiselect' | 'number';
  required: boolean;
  help: string;
  options?: { value: string; label: string }[];
  defaultValue?: string | number | string[];
}

export interface ConnectorDescriptor {
  id: string;
  name: string;
  vendor: string;
  summary: string;
  credentials: CredentialField[];
  parameters: ParameterField[];
  scopes: string[];
  docsUrl: string;
  timeSeries: boolean;
}

export interface Resource {
  id: string;
  name: string;
  detail?: string;
}

// --- dashboards ---------------------------------------------------------------

export type ComponentKind = 'chart' | 'scorecard' | 'table' | 'pivot' | 'forecast';

export interface DashboardComponent {
  id: string;
  kind: ComponentKind;
  title: string;
  layout: { x: number; y: number; w: number; h: number };
  config: Record<string, unknown>;
}

export interface Dashboard {
  id: string;
  datasetId: string;
  name: string;
  description: string | null;
  components: DashboardComponent[];
  filters: Filter[];
  createdAt: string;
  updatedAt: string;
}

export interface LookerField {
  id: string;
  name: string;
  dataType: 'NUMBER' | 'TEXT' | 'YEAR_MONTH_DAY' | 'BOOLEAN';
  semanticType: string;
  conceptType: 'DIMENSION' | 'METRIC';
  defaultAggregation: string;
  completeness: number;
}

export interface LookerElement {
  id: string;
  type: string;
  title: string;
  dimension?: string;
  metric?: string;
  aggregation: string;
  sort?: string;
  rationale?: string;
}

export interface LookerTemplate {
  version: string;
  name: string;
  generatedAt: string;
  dataSource: {
    alias: string;
    name: string;
    suggestedConnector: string;
    fields: LookerField[];
    calculatedFields: { name: string; formula: string; why: string }[];
  };
  pages: { id: string; name: string; elements: LookerElement[] }[];
  links: { createReport: string; explore: string | null; copiesTemplate: boolean } | null;
}

export interface UploadResult {
  dataset: Dataset;
  report: Report;
  chartData: { id: string; buckets: Bucket[] }[];
  skippedRows: number;
  warnings: string[];
  elapsedMs: number;
}

export interface Health {
  ok: boolean;
  version: string;
  env: string;
  storage: 'memory' | 'postgres';
  narrator: { provider: string; model: string | null };
}

export interface SeriesSpec {
  filters?: Filter[];
  timeColumn?: string;
  measure?: string;
  aggregation?: Aggregation;
  period?: CalendarPeriod;
}

// -----------------------------------------------------------------------------
// Calls
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// AI analytics — the zero-configuration dashboard
// -----------------------------------------------------------------------------

/**
 * These mirror `@datavantage/autodash` rather than importing it.
 *
 * The web app is not a workspace member of the server packages — it bundles for
 * the browser and must not pull a Node-targeted dependency graph into Vite. The
 * duplication is real, and the guard against drift is that the API is versioned
 * (`spec.version`) and the server rejects a spec it does not recognise.
 */
export type BlockKind =
  | 'kpi'
  | 'trend'
  | 'comparison'
  | 'breakdown'
  | 'share'
  | 'heatmap'
  | 'performers'
  | 'drivers'
  | 'distribution'
  | 'correlation'
  | 'pivot'
  | 'anomalies'
  | 'forecast'
  | 'table';

export type BlockWidth = 'third' | 'half' | 'two-thirds' | 'full';

export interface BlockSpec {
  id: string;
  kind: BlockKind;
  title: string;
  subtitle: string;
  rationale: string;
  width: BlockWidth;
  score: number;
  /** Kind-specific fields. Read through the narrowing helpers in AutoDashboard. */
  [key: string]: unknown;
}

export interface ColumnRoles {
  primaryMeasure: string | null;
  measures: string[];
  primaryDimension: string | null;
  dimensions: string[];
  dateColumn: string | null;
  excluded: { column: string; reason: string }[];
}

export interface FilterControl {
  column: string;
  label: string;
  kind: 'multiSelect' | 'dateRange' | 'numericRange';
  options: string[];
  truncated: boolean;
  min?: string;
  max?: string;
}

export interface DashboardSpec {
  version: 1;
  title: string;
  roles: ColumnRoles;
  blocks: BlockSpec[];
  filters: FilterControl[];
  searchColumns: string[];
  limitations: string[];
}

export interface AutoPoint {
  key: string;
  value: number;
  count: number;
}

export interface AutoSeriesPoint {
  key: string;
  values: (number | null)[];
}

export interface Performer extends AutoPoint {
  rank: number;
  share: number | null;
}

export interface Driver {
  key: string;
  current: number;
  previous: number;
  delta: number;
  contribution: number;
  changePercent: number | null;
}

export interface ComparisonEntry {
  period: CalendarPeriod;
  label: string;
  current: number;
  previous: number;
  changePercent: number | null;
  direction: 'up' | 'down' | 'flat';
  comparable: boolean;
  currentLabel: string;
  previousLabel: string;
}

export type BlockData =
  | { kind: 'kpi'; cards: Scorecard[] }
  | {
      kind: 'trend';
      series: string[];
      points: AutoSeriesPoint[];
      format: string;
      change: { percent: number | null; direction: 'up' | 'down' | 'flat' } | null;
    }
  | { kind: 'comparison'; entries: ComparisonEntry[]; format: string }
  | { kind: 'breakdown'; points: AutoPoint[]; other: AutoPoint | null; totalGroups: number; format: string }
  | {
      kind: 'share';
      points: AutoPoint[];
      other: AutoPoint | null;
      total: number;
      format: string;
      additive: boolean;
    }
  | {
      kind: 'heatmap';
      rowKeys: string[];
      columnKeys: string[];
      cells: { row: number; column: number; value: number | null }[];
      min: number;
      max: number;
      format: string;
    }
  | {
      kind: 'performers';
      top: Performer[];
      bottom: Performer[];
      totalGroups: number;
      total: number;
      format: string;
      additive: boolean;
    }
  | {
      kind: 'drivers';
      currentLabel: string;
      previousLabel: string;
      total: { current: number; previous: number; delta: number };
      gainers: Driver[];
      losers: Driver[];
      format: string;
      comparable: boolean;
    }
  | {
      kind: 'distribution';
      bins: { from: number; to: number; count: number }[];
      median: number;
      mean: number;
      skewness: number;
      outlierCount: number;
      format: string;
    }
  | {
      kind: 'correlation';
      points: { x: number; y: number }[];
      r: number;
      n: number;
      strength: string;
      direction: 'positive' | 'negative';
      sampled: boolean;
    }
  | { kind: 'pivot'; table: PivotTable; format: string }
  | {
      kind: 'anomalies';
      points: { key: string; actual: number; expected: number; deviation: number; direction: 'above' | 'below' }[];
      checked: number;
      format: string;
      unavailable: string | null;
    }
  | {
      kind: 'forecast';
      history: { key: string; value: number }[];
      points: { key: string; value: number; lower: number; upper: number }[];
      model: string;
      seasonality: string[];
      format: string;
      unavailable: string | null;
    }
  | { kind: 'table'; columns: string[]; rows: (string | number | boolean | null)[][]; totalRows: number };

export interface RenderedBlock {
  spec: BlockSpec;
  data: BlockData | null;
  unavailable: string | null;
}

export interface Highlight {
  id: string;
  kind: 'trend' | 'anomaly' | 'driver' | 'concentration' | 'risk' | 'opportunity' | 'quality';
  severity: 'info' | 'notable' | 'warning';
  headline: string;
  detail: string;
  blockId: string | null;
  subject: string | null;
  drillDown: { column: string; value: string } | null;
  evidence: Evidence[];
}

export interface RenderedDashboard {
  spec: DashboardSpec;
  blocks: RenderedBlock[];
  highlights: Highlight[];
  summary: string[];
  recommendations: string[];
  insights: Insight[];
  rowCount: number;
  totalRowCount: number;
  appliedFilters: Filter[];
  elapsedMs: number;
}

export interface AnalyzeResult {
  datasetId: string;
  reportId: string;
  fileName: string;
  /** True for PDF/log/text, where the table layout was guessed rather than read. */
  structureInferred: boolean;
  warnings: string[];
  dashboard: RenderedDashboard;
  elapsedMs: number;
}

// -----------------------------------------------------------------------------
// Client
// -----------------------------------------------------------------------------

export const api = {
  health: () => request<Health>('/api/health'),

  upload(file: File, onProgress?: (fraction: number) => void): Promise<UploadResult> {
    // XHR rather than fetch: a large upload with no progress looks like a hang,
    // and fetch still cannot report request-body progress.
    return new Promise((resolve, reject) => {
      const form = new FormData();
      form.append('file', file);

      // XHR bypasses `request`, so the session and workspace have to be set here
      // too â€” an upload that lands in the wrong workspace is worse than one that
      // fails outright.
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/datasets');
      xhr.withCredentials = true;
      for (const [key, value] of Object.entries(workspaceHeader())) {
        xhr.setRequestHeader(key, value);
      }

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total);
      });

      xhr.addEventListener('load', () => {
        let body: Record<string, unknown> = {};
        try {
          body = JSON.parse(xhr.responseText) as Record<string, unknown>;
        } catch {
          reject(new ApiFailure('The server returned an unreadable response.', 'Try again.', xhr.status));
          return;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(body as unknown as UploadResult);
        } else {
          reject(
            new ApiFailure(
              (body['error'] as string) ?? 'The upload failed.',
              (body['remedy'] as string) ?? 'Try again.',
              xhr.status,
            ),
          );
        }
      });

      xhr.addEventListener('error', () =>
        reject(new ApiFailure('Could not reach the server.', 'Check your connection.', 0)),
      );

      xhr.send(form);
    });
  },

  listDatasets: () => request<{ datasets: DatasetSummary[] }>('/api/datasets'),
  getDataset: (id: string) => request<{ dataset: Dataset; warnings: string[] }>(`/api/datasets/${id}`),
  deleteDataset: (id: string) =>
    request<{ deleted: boolean }>(`/api/datasets/${id}`, { method: 'DELETE' }),

  rows: (id: string, offset: number, limit: number) =>
    request<{ columns: string[]; rows: Record<string, unknown>[]; total: number }>(
      `/api/datasets/${id}/rows?offset=${offset}&limit=${limit}`,
    ),

  filterOptions: (id: string) =>
    request<{ options: Record<string, string[]> }>(`/api/datasets/${id}/filter-options`),

  query: (
    id: string,
    spec: {
      filters?: Filter[];
      groupBy?: string;
      measure?: string;
      aggregation: Aggregation;
      limit?: number;
      sort?: string;
      period?: CalendarPeriod;
    },
  ) => request<{ result: AggregateResult; matchedRows: number }>(`/api/datasets/${id}/query`, json(spec)),

  pivot: (
    id: string,
    spec: {
      filters?: Filter[];
      rowFields: string[];
      columnField?: string;
      measure?: string;
      aggregation: Aggregation;
      rowLimit?: number;
      columnLimit?: number;
    },
  ) => request<{ pivot: PivotTable; matchedRows: number }>(`/api/datasets/${id}/pivot`, json(spec)),

  // --- forecasting ---

  forecast: (
    id: string,
    spec: SeriesSpec & {
      horizon?: number;
      model?: ForecastModelName;
      seasonality?: boolean;
      changepoints?: number;
      confidenceLevel?: 0.8 | 0.9 | 0.95 | 0.99;
      holidays?: { name: string; dates: string[]; lowerWindow?: number; upperWindow?: number }[];
    },
  ) =>
    request<{
      forecast: Projection;
      timeColumn: string;
      measure: string | null;
      period: string | null;
      points: number;
    }>(`/api/datasets/${id}/forecast`, json(spec)),

  backtest: (id: string, spec: SeriesSpec & { horizon?: number; folds?: number }) =>
    request<{ comparison: ModelComparison; timeColumn: string; measure: string | null; points: number }>(
      `/api/datasets/${id}/backtest`,
      json(spec),
    ),

  anomalies: (id: string, spec: SeriesSpec & { sensitivity?: number }) =>
    request<{
      anomalies: Anomaly[];
      examined: number;
      timeColumn: string;
      measure: string | null;
      model: { seasonalities: Seasonality[]; residualStdError: number; notes: string[] };
    }>(`/api/datasets/${id}/anomalies`, json(spec)),

  // --- connectors ---

  listConnectors: () => request<{ connectors: ConnectorDescriptor[] }>('/api/connectors'),

  verifyConnector: (id: string, credentials: Record<string, string>) =>
    request<{ connector: string; resources: Resource[] }>(
      `/api/connectors/${id}/verify`,
      json({ credentials }),
    ),

  importConnector: (
    id: string,
    body: {
      credentials: Record<string, string>;
      parameters: Record<string, string | number | string[]>;
      startDate?: string;
      endDate?: string;
      name?: string;
    },
  ) =>
    request<{
      dataset: Dataset;
      report: Report;
      source: { connector: string; fetchedAt: string; totalAvailable: number | null };
      warnings: string[];
    }>(`/api/connectors/${id}/import`, json(body)),

  // --- reports ---

  listReports: () => request<{ reports: Report[] }>('/api/reports'),
  getReport: (id: string) => request<{ report: Report }>(`/api/reports/${id}`),
  deleteReport: (id: string) => request<{ deleted: boolean }>(`/api/reports/${id}`, { method: 'DELETE' }),

  narrate: (id: string, body: { question?: string; audience?: 'executive' | 'analyst' }) =>
    request<{ narration: Narrative }>(`/api/reports/${id}/narrate`, json(body)),

  exportUrl: (reportId: string, format: 'csv' | 'xlsx' | 'pdf' | 'looker' | 'looker-package') =>
    `/api/reports/${reportId}/export/${format}`,

  // --- Looker Studio ---

  lookerTemplate: (reportId: string) =>
    request<{ template: LookerTemplate; templateReportConfigured: boolean }>(
      `/api/reports/${reportId}/looker/template`,
    ),

  lookerPublish: (
    reportId: string,
    body: { credentials: Record<string, string>; rows?: 'all' | 'sample' },
  ) =>
    request<{
      spreadsheet: { id: string; url: string; rowsWritten: number; truncated: boolean };
      links: { createReport: string; explore: string | null; copiesTemplate: boolean } | null;
      warnings: string[];
    }>(`/api/reports/${reportId}/looker/publish`, json(body)),

  lookerLink: (reportId: string, binding: Record<string, string>) =>
    request<{ url: string; copiesTemplate: boolean }>(
      `/api/reports/${reportId}/looker/link`,
      json(binding),
    ),

  // --- dashboards ---

  listDashboards: () => request<{ dashboards: Dashboard[] }>('/api/dashboards'),
  getDashboard: (id: string) => request<{ dashboard: Dashboard }>(`/api/dashboards/${id}`),

  createDashboard: (body: {
    datasetId: string;
    name: string;
    description?: string | null;
    components?: DashboardComponent[];
    filters?: Filter[];
  }) => request<{ dashboard: Dashboard }>('/api/dashboards', json(body)),

  updateDashboard: (
    id: string,
    body: {
      name?: string;
      description?: string | null;
      components?: DashboardComponent[];
      filters?: Filter[];
    },
  ) =>
    request<{ dashboard: Dashboard }>(`/api/dashboards/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }),

  deleteDashboard: (id: string) =>
    request<{ deleted: boolean }>(`/api/dashboards/${id}`, { method: 'DELETE' }),

  // ---------------------------------------------------------------------------
  // Accounts and workspaces
  // ---------------------------------------------------------------------------

  signup: (body: { email: string; name: string; password: string }) =>
    request<Session>('/api/auth/signup', json(body)),

  login: (body: { email: string; password: string }) =>
    request<Session>('/api/auth/login', json(body)),

  logout: () => request<{ signedOut: boolean }>('/api/auth/logout', { method: 'POST' }),

  me: () => request<Session>('/api/auth/me'),

  workspace: () => request<WorkspaceDetail>('/api/workspace'),

  updateWorkspace: (body: { name?: string; branding?: Partial<Branding> }) =>
    request<{ workspace: Workspace }>('/api/workspace', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }),


  addMember: (body: { email: string; role: 'admin' | 'member' | 'viewer' }) =>
    request<{ member: unknown }>('/api/workspace/members', json(body)),

  removeMember: (userId: string) =>
    request<{ removed: boolean }>(`/api/workspace/members/${userId}`, { method: 'DELETE' }),

  updateMemberRole: (userId: string, role: 'admin' | 'member' | 'viewer') =>
    request<{ member: { userId: string; email: string; role: string } }>(
      `/api/workspace/members/${userId}`,
      {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ role }),
      },
    ),

  /** One aggregated read for the admin overview page (admin-gated). */
  adminOverview: () => request<AdminOverview>('/api/workspace/overview'),

  // ---------------------------------------------------------------------------
  // Sharing
  // ---------------------------------------------------------------------------

  listShares: (reportId?: string) =>
    request<{ shares: Share[] }>(
      `/api/shares${reportId === undefined ? '' : `?reportId=${encodeURIComponent(reportId)}`}`,
    ),

  createShare: (body: { reportId: string; expiresInDays?: number; password?: string }) =>
    request<{ share: Share; url: string; code: string }>('/api/shares', json(body)),

  revokeShare: (id: string) =>
    request<{ revoked: boolean }>(`/api/shares/${id}`, { method: 'DELETE' }),

  /** No session required â€” this is the anonymous viewer's only call. */
  viewShare: (code: string, password?: string) =>
    request<SharedReport>(
      `/api/public/shares/${encodeURIComponent(code)}`,
      json(password === undefined ? {} : { password }),
    ),

  // ---------------------------------------------------------------------------
  // Schedules
  // ---------------------------------------------------------------------------

  listSchedules: () =>
    request<{ schedules: Schedule[]; enabled: boolean; emailProvider: string }>('/api/schedules'),

  createSchedule: (body: Record<string, unknown>) =>
    request<{ schedule: Schedule }>('/api/schedules', json(body)),

  updateSchedule: (id: string, body: Record<string, unknown>) =>
    request<{ schedule: Schedule }>(`/api/schedules/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }),

  deleteSchedule: (id: string) =>
    request<{ deleted: boolean }>(`/api/schedules/${id}`, { method: 'DELETE' }),

  runSchedule: (id: string) =>
    request<{ schedule: Schedule; status: string | null; error: string | null }>(
      `/api/schedules/${id}/run`,
      json({}),
    ),

  // ---------------------------------------------------------------------------
  // Alerts — rules, channels, and the evaluation history
  // ---------------------------------------------------------------------------

  listAlertRules: () => request<{ rules: AlertRule[] }>('/api/alerts/rules'),

  createAlertRule: (body: Record<string, unknown>) =>
    request<{ rule: AlertRule }>('/api/alerts/rules', json(body)),

  updateAlertRule: (id: string, body: Record<string, unknown>) =>
    request<{ rule: AlertRule }>(`/api/alerts/rules/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }),

  deleteAlertRule: (id: string) =>
    request<{ deleted: boolean }>(`/api/alerts/rules/${id}`, { method: 'DELETE' }),

  listAlertEvents: (ruleId?: string) =>
    request<{ events: AlertEvent[] }>(
      `/api/alerts/events${ruleId === undefined ? '' : `?ruleId=${encodeURIComponent(ruleId)}`}`,
    ),

  listAlertChannels: () => request<{ channels: AlertChannel[] }>('/api/alerts/channels'),

  createAlertChannel: (body: Record<string, unknown>) =>
    request<{ channel: AlertChannel }>('/api/alerts/channels', json(body)),

  updateAlertChannel: (id: string, body: Record<string, unknown>) =>
    request<{ channel: AlertChannel }>(`/api/alerts/channels/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }),

  deleteAlertChannel: (id: string) =>
    request<{ deleted: boolean }>(`/api/alerts/channels/${id}`, { method: 'DELETE' }),

  testAlertChannel: (id: string) =>
    request<{ channel: AlertChannel; delivered: boolean; detail: string }>(
      `/api/alerts/channels/${id}/test`,
      json({}),
    ),

  // -- AI analytics ---------------------------------------------------------

  /** Upload and analyse in one call. Returns the whole first dashboard. */
  analyzeFile(file: File, onProgress?: (fraction: number) => void): Promise<AnalyzeResult> {
    return new Promise((resolve, reject) => {
      const form = new FormData();
      form.append('file', file);

      // XHR bypasses `request`, so the session and workspace headers have to
      // be set here too — an upload that lands in the wrong workspace is far
      // worse than one that fails outright.
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/ai/analyze');
      xhr.withCredentials = true;
      for (const [key, value] of Object.entries(workspaceHeader())) {
        xhr.setRequestHeader(key, value);
      }

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total);
      });

      xhr.addEventListener('load', () => {
        let body: Record<string, unknown> = {};
        try {
          body = JSON.parse(xhr.responseText) as Record<string, unknown>;
        } catch {
          reject(new ApiFailure('The server returned an unreadable response.', 'Try again.', xhr.status));
          return;
        }
        if (xhr.status >= 200 && xhr.status < 300) resolve(body as unknown as AnalyzeResult);
        else {
          reject(
            new ApiFailure(
              (body['error'] as string) ?? 'The analysis failed.',
              (body['remedy'] as string) ?? 'Try again.',
              xhr.status,
            ),
          );
        }
      });

      xhr.addEventListener('error', () =>
        reject(new ApiFailure('Could not reach the server.', 'Check your connection.', 0)),
      );

      xhr.send(form);
    });
  },

  /** Compose a dashboard for a dataset that was uploaded earlier. */
  composeDashboard: (datasetId: string) =>
    request<{ datasetId: string; fileName: string; warnings: string[]; dashboard: RenderedDashboard }>(
      `/api/ai/datasets/${datasetId}/compose`,
      json({}),
    ),

  /**
   * Re-render every block under a new filter set.
   *
   * One request per interaction, not one per chart — which is both why it is
   * fast and why two blocks can never disagree about the same number.
   */
  renderDashboard: (datasetId: string, spec: DashboardSpec, filters: Filter[], search: string) =>
    request<{ dashboard: RenderedDashboard }>(
      `/api/ai/datasets/${datasetId}/render`,
      json({ spec, filters, search }),
    ),

  narrateDashboard: (datasetId: string, spec: DashboardSpec, filters: Filter[], search: string) =>
    request<{ narrative: Narrative; allowedFigures: number }>(
      `/api/ai/datasets/${datasetId}/narrate`,
      json({ spec, filters, search }),
    ),

  // ---------------------------------------------------------------------------
  // Template marketplace
  // ---------------------------------------------------------------------------

  listTemplates: () =>
    request<{ templates: { id: string; vertical: string; name: string; description: string; icon: string; priceCents: number; planRequired: string; installCount: number; fieldCount: number; chartCount: number }[] }>('/api/templates'),

  getTemplateDetail: (id: string) =>
    request<{ template: unknown }>(`/api/templates/${id}`),

  matchTemplates: (columns: string[]) =>
    request<{ matches: { templateId: string; name: string; icon: string; score: number; matchedFields: string[]; missingRequired: string[] }[] }>(
      '/api/templates/match',
      json({ columns }),
    ),

  applyTemplate: (templateId: string, datasetId: string) =>
    request<{ template: unknown; fieldMapping: Record<string, string>; warnings: string[] }>(
      `/api/templates/${templateId}/apply`,
      json({ datasetId }),
    ),

  // ---------------------------------------------------------------------------
  // Power BI
  // ---------------------------------------------------------------------------

  powerBiTemplate: (reportId: string) =>
    request<{ template: unknown }>(`/api/reports/${reportId}/powerbi/template`, json({})),

  powerBiPush: (reportId: string, body: { accessToken: string; workspaceId: string; rows?: 'all' | 'sample' }) =>
    request<{ datasetId: string; webUrl: string; rowsPushed: number }>(
      `/api/reports/${reportId}/powerbi/push`,
      json(body),
    ),

  // ---------------------------------------------------------------------------
  // Client portals
  // ---------------------------------------------------------------------------

  listPortals: () =>
    request<{ portals: { id: string; name: string; slug: string; active: boolean; reportCount: number; dashboardCount: number; createdAt: string }[] }>('/api/portals'),

  getPortal: (id: string) =>
    request<{ portal: unknown; accessTokens: { clientEmail: string; clientName: string; createdAt: string; expiresAt: string; accessCount: number }[] }>(`/api/portals/${id}`),

  createPortal: (body: { name: string; branding?: { companyName?: string; accentColor?: string } }) =>
    request<{ portal: unknown }>('/api/portals', json(body)),

  updatePortal: (id: string, body: { name?: string; active?: boolean }) =>
    request<{ portal: unknown }>(`/api/portals/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }),

  deletePortal: (id: string) =>
    request<{ deleted: boolean }>(`/api/portals/${id}`, { method: 'DELETE' }),

  createPortalAccess: (portalId: string, body: { clientName: string; clientEmail: string }) =>
    request<{ token: string; accessUrl: string; expiresAt: string }>(`/api/portals/${portalId}/access`, json(body)),

  /**
   * The anonymous portal visitor's only call. The token travels in the POST
   * body, never the URL — it arrived in the page's `#key=` fragment, which the
   * browser does not send to the server.
   */
  viewPortal: (slug: string, token: string) =>
    request<PortalViewData>(`/api/public/portals/${encodeURIComponent(slug)}`, json({ token })),

  // ---------------------------------------------------------------------------
  // Client reports
  // ---------------------------------------------------------------------------

  composeClientReport: (reportId: string, body: { clientName: string; companyName?: string; period: string; accentColor?: string }) =>
    request<{ html: string; reportTitle: string }>(`/api/reports/${reportId}/client-report/compose`, json(body)),

  sendClientReport: (reportId: string, body: { clientName: string; clientEmail: string; companyName?: string; period: string; accentColor?: string }) =>
    request<{ sent: boolean; detail: string; reportUrl: string }>(`/api/reports/${reportId}/client-report/send`, json(body)),

  // Web analytics
  getTraffic: (params: { websiteId: string; start: string; end: string; filters: TrafficFilter[]; compare?: boolean }) =>
    request<TrafficResponse>(
      `/api/revenue/traffic?${new URLSearchParams({
        websiteId: params.websiteId,
        start: params.start,
        end: params.end,
        compare: params.compare === false ? 'none' : 'previous',
        ...(params.filters.length > 0 ? { filters: JSON.stringify(params.filters) } : {}),
      }).toString()}`,
    ),

  getRealtime: (websiteId: string) =>
    request<RealtimeResponse>(`/api/revenue/realtime?websiteId=${encodeURIComponent(websiteId)}`),

  getMarketing: (params: {
    startDate: string;
    endDate: string;
    cogs: number;
    fees: number;
    shipping: number;
    fixed: number;
    model: string;
    window: number;
  }) =>
    request<MarketingResponse>(
      `/api/revenue/marketing?${new URLSearchParams(
        Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)])),
      ).toString()}`,
    ),

  // Revenue analytics
  listRevenueWebsites: () =>
    request<{ websites: RevenueWebsite[] }>('/api/revenue/websites'),

  createRevenueWebsite: (domain: string) =>
    request<{ website: RevenueWebsite }>('/api/revenue/websites', json({ domain })),

  getRevenueSnippet: (websiteId: string) =>
    request<{ snippet: string; website: RevenueWebsite }>(`/api/revenue/websites/${websiteId}/snippet`),

  deleteRevenueWebsite: (websiteId: string) =>
    request<void>(`/api/revenue/websites/${websiteId}`, { method: 'DELETE' }),

  getRevenueMetrics: (params: { websiteId: string; startDate: string; endDate: string }) =>
    request<{ metrics: RevenueMetrics }>(`/api/revenue/metrics?websiteId=${params.websiteId}&startDate=${params.startDate}&endDate=${params.endDate}`),

  getRevenueChannelBreakdown: (params: { websiteId?: string; startDate: string; endDate: string }) =>
    request<{ breakdown: ChannelBreakdown[] }>(
      `/api/revenue/metrics/channels?startDate=${params.startDate}&endDate=${params.endDate}${params.websiteId !== undefined ? `&websiteId=${params.websiteId}` : ''}`,
    ),

  compareAttribution: (params: { startDate: string; endDate: string; model?: AttributionModelName }) =>
    request<{ comparison: AttributionComparisonData; warning: string | null }>(
      `/api/revenue/attribution/compare?startDate=${params.startDate}&endDate=${params.endDate}${params.model !== undefined ? `&model=${params.model}` : ''}`,
    ),

  listCampaignSpend: (params?: { startDate?: string; endDate?: string }) =>
    request<{ spend: CampaignSpendEntry[] }>(
      `/api/revenue/spend${params?.startDate !== undefined || params?.endDate !== undefined
        ? `?${[
            params?.startDate !== undefined ? `startDate=${params.startDate}` : '',
            params?.endDate !== undefined ? `endDate=${params.endDate}` : '',
          ].filter(Boolean).join('&')}`
        : ''}`,
    ),

  createCampaignSpend: (body: {
    campaign: string;
    channel: string;
    provider?: string;
    amount: number;
    currency?: string;
    periodStart: string;
    periodEnd: string;
  }) => request<{ spend: CampaignSpendEntry }>('/api/revenue/spend', json(body)),

  deleteCampaignSpend: (id: string) =>
    request<void>(`/api/revenue/spend/${id}`, { method: 'DELETE' }),

  revenueImportSuggest: (datasetId: string) =>
    request<{ mapping: RevenueImportMapping; columns: RevenueImportColumn[] }>(
      '/api/revenue/import/suggest',
      json({ datasetId }),
    ),

  revenueImportRun: (body: {
    datasetId: string;
    mode: RevenueImportMode;
    mapping: RevenueImportMapping;
  }) => request<RevenueImportResult>('/api/revenue/import/run', json(body)),

  getRevenueCustomers: (limit?: number, offset?: number) =>
    request<{ customers: RevenueCustomer[] }>(`/api/revenue/customers?limit=${limit ?? 100}&offset=${offset ?? 0}`),

  getCustomerJourney: (customerId: string) =>
    request<{ journey: CustomerJourneyData }>(`/api/revenue/journeys/${customerId}`),

  runRevenueFunnel: (body: {
    steps: FunnelStepInput[];
    startDate: string;
    endDate: string;
    websiteId?: string;
  }) => request<{ funnel: FunnelResultData }>('/api/revenue/funnels', json(body)),

  getRevenueRetention: (params: {
    startDate: string;
    endDate: string;
    grain?: RetentionGrainName;
    entity?: RetentionEntityName;
    websiteId?: string;
  }) =>
    request<{ retention: RetentionGridData }>(
      `/api/revenue/retention?${[
        `startDate=${params.startDate}`,
        `endDate=${params.endDate}`,
        params.grain !== undefined ? `grain=${params.grain}` : '',
        params.entity !== undefined ? `entity=${params.entity}` : '',
        params.websiteId !== undefined ? `websiteId=${params.websiteId}` : '',
      ].filter(Boolean).join('&')}`,
    ),
};

// -----------------------------------------------------------------------------
// Account shapes
// -----------------------------------------------------------------------------

export type Plan = 'free' | 'pro' | 'agency';
export type Role = 'owner' | 'admin' | 'member' | 'viewer';

export interface Branding {
  companyName: string | null;
  logoUrl: string | null;
  accentColor: string | null;
  footerNote: string | null;
  showPoweredBy: boolean;
}

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  plan: Plan;
  branding: Branding;
  role: Role;
}

export interface User {
  id: string;
  email: string;
  name: string;
  createdAt: string;
}

export interface Session {
  user: User;
  workspaces: Workspace[];
}

export interface PlanLimits {
  datasetsPerMonth: number;
  rowsPerMonth: number;
  narrationsPerMonth: number;
  scheduledDatasets: number;
  members: number;
  whiteLabel: boolean;
  shareLinks: boolean;
}

export interface Usage {
  datasetsCreated: number;
  rowsIngested: number;
  narrationsRun: number;
  scheduledRuns: number;
  exportsGenerated: number;
}

export interface WorkspaceDetail {
  workspace: Workspace;
  limits: PlanLimits;
  usage: Usage;
  members: { userId: string; email: string; name: string; role: Role; joinedAt: string }[];
}

/** What `/api/workspace/overview` returns: counts plus plan usage in one read. */
export interface AdminOverview {
  workspace: Workspace;
  limits: PlanLimits;
  usage: Usage;
  counts: {
    members: number;
    portals: number;
    datasets: number;
    reports: number;
    dashboards: number;
  };
  members: { userId: string; email: string; name: string; role: Role; joinedAt: string }[];
  portals: { id: string; name: string; slug: string; active: boolean; reportCount: number; createdAt: string }[];
}

export interface Share {
  id: string;
  reportId: string;
  createdAt: string;
  expiresAt: string | null;
  hasPassword: boolean;
  viewCount: number;
  lastViewedAt: string | null;
  revoked: boolean;
}

export interface Schedule {
  id: string;
  datasetId: string;
  connectorId: string;
  name: string;
  cadence: 'daily' | 'weekly' | 'monthly';
  hourUtc: number;
  lookbackDays: number;
  recipients: string[];
  channelIds: string[];
  alwaysNotify: boolean;
  alertOnAnomaly: boolean;
  active: boolean;
  lastRunAt: string | null;
  lastRunStatus: 'ok' | 'failed' | null;
  lastRunError: string | null;
  nextRunAt: string;
}

// ---------------------------------------------------------------------------
// Alerting — channels, rules, and the evaluation history. Mirrors of
// routes/alerts.ts.
// ---------------------------------------------------------------------------

export type ChannelType = 'email' | 'webhook' | 'slack';

export interface AlertChannel {
  id: string;
  type: ChannelType;
  name: string;
  /** Full for email, masked (`https://host/…abcd`) for webhook and Slack. */
  target: string;
  masked: boolean;
  hasSecret: boolean;
  active: boolean;
  createdAt: string;
  lastDeliveryAt: string | null;
  lastDeliveryStatus: 'ok' | 'failed' | null;
  lastDeliveryError: string | null;
}

export type AlertCondition = 'above' | 'below' | 'changeByPercent';

export interface AlertRule {
  id: string;
  datasetId: string;
  name: string;
  metric: { column: string | null; aggregation: Aggregation };
  condition: AlertCondition;
  threshold: number;
  cooldownMinutes: number;
  channelIds: string[];
  active: boolean;
  createdAt: string;
  lastEvaluatedAt: string | null;
  lastFiredAt: string | null;
  lastState: 'ok' | 'firing' | null;
  lastValue: number | null;
}

export interface AlertDelivery {
  channelId: string;
  channelType: ChannelType;
  channelName: string;
  delivered: boolean;
  error: string | null;
  at: string;
}

export interface AlertEvent {
  id: string;
  ruleId: string;
  datasetId: string;
  state: 'ok' | 'firing';
  value: number | null;
  threshold: number;
  condition: AlertCondition;
  suppressed: boolean;
  deliveries: AlertDelivery[];
  error: string | null;
  evaluatedAt: string;
}

/** What a share link exposes â€” deliberately narrower than a report. */
export interface SharedReport {
  report: {
    title: string;
    createdAt: string;
    scorecards: Scorecard[];
    insights: Insight[];
    quality: Quality;
    narrative: Narrative | null;
    charts: ChartSuggestion[];
  };
  branding: Branding | null;
}

/**
 * What a client portal exposes: the same read-only summary shape as a share
 * link, for each report the portal was granted. No rows, no query access.
 */
export interface PortalViewData {
  portal: {
    name: string;
    branding: {
      companyName: string;
      logoUrl: string | null;
      accentColor: string;
      primaryColor: string;
      footerText: string | null;
      showDataVantageBranding: boolean;
    };
  };
  /** Workspace branding, as a fallback for whatever the portal does not set. */
  branding: Branding | null;
  client: { name: string };
  reports: {
    id: string;
    title: string;
    createdAt: string;
    scorecards: Scorecard[];
    insights: Insight[];
    quality: Quality;
    narrative: Narrative | null;
    charts: ChartSuggestion[];
  }[];
  viewedAt: string;
}

// -----------------------------------------------------------------------------
// Revenue analytics shapes
// -----------------------------------------------------------------------------

// --- web analytics ---

export type TrafficDimension =
  | 'page' | 'entry_page' | 'exit_page' | 'source' | 'referrer' | 'channel'
  | 'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_term' | 'utm_content'
  | 'country' | 'city' | 'device' | 'browser' | 'os' | 'goal';

export interface TrafficFilter {
  dimension: TrafficDimension;
  value: string;
}

export interface TrafficSummary {
  visitors: number;
  visits: number;
  pageviews: number;
  viewsPerVisit: number | null;
  bounceRate: number | null;
  avgVisitDurationMs: number | null;
  revenue: number;
  attributedRevenue: number;
  conversions: number;
  conversionRate: number | null;
  revenuePerVisitor: number | null;
}

export interface TrafficRow {
  key: string;
  visitors: number;
  visits: number;
  pageviews: number;
  events: number;
  bounceRate: number | null;
  avgVisitDurationMs: number | null;
  revenue: number;
  conversions: number;
  conversionRate: number | null;
  revenuePerVisitor: number | null;
  share: number | null;
}

export interface TrafficPoint {
  bucket: string;
  visitors: number;
  visits: number;
  pageviews: number;
  revenue: number;
}

export interface TrafficResponse {
  report: {
    summary: TrafficSummary;
    interval: 'hour' | 'day' | 'month';
    series: TrafficPoint[];
    breakdowns: Record<TrafficDimension, TrafficRow[]>;
    filters: TrafficFilter[];
    eventsConsidered: number;
  };
  previous: { summary: TrafficSummary; series: TrafficPoint[]; range: { start: string; end: string } } | null;
  range: { start: string; end: string };
  revenueScope: 'workspace' | 'attributed';
  unattributedRevenue: number;
  website: { id: string; domain: string };
}

export interface RealtimeResponse {
  realtime: {
    visitorsNow: number;
    visitorsLast30: number;
    pageviewsLast30: number;
    perMinute: { minute: string; pageviews: number; visitors: number }[];
    activePages: { key: string; visitors: number }[];
    sources: { key: string; visitors: number }[];
    countries: { key: string; visitors: number }[];
    devices: { key: string; visitors: number }[];
    feed: {
      at: string;
      kind: 'pageview' | 'goal' | 'identify';
      name: string | null;
      path: string;
      source: string;
      country: string | null;
      device: string;
      browser: string | null;
      visitor: string;
    }[];
  };
  payments: { amount: number; currency: string; provider: string; at: string }[];
  now: string;
  website: { id: string; domain: string };
}

export interface MarketingSummary {
  grossRevenue: number;
  refunds: number;
  netRevenue: number;
  orders: number;
  averageOrderValue: number | null;
  adSpend: number;
  mer: number | null;
  adSpendShare: number | null;
  newCustomers: number;
  returningCustomers: number;
  newCustomerRevenue: number;
  returningCustomerRevenue: number;
  ncRoas: number | null;
  ncac: number | null;
  cogs: number;
  paymentFees: number;
  shipping: number;
  fixedCosts: number;
  grossProfit: number;
  contributionProfit: number;
  netProfit: number;
  netMargin: number | null;
  breakEvenMer: number | null;
  costs: { cogsPercent: number; paymentFeePercent: number; shippingPerOrder: number; fixedCostsPerDay: number };
}

export interface MarketingDay {
  date: string;
  grossRevenue: number;
  netRevenue: number;
  orders: number;
  newCustomers: number;
  adSpend: number;
  mer: number | null;
  contributionProfit: number;
}

export type MixResult =
  | {
      ok: true;
      observations: number;
      adstockDecay: number;
      baseline: number;
      baselineShare: number;
      totalRevenue: number;
      r2: number;
      mape: number | null;
      channels: { channel: string; spend: number; contribution: number; roi: number | null; share: number | null; coefficient: number }[];
      warnings: string[];
    }
  | { ok: false; reason: string; observations: number };

export interface MarketingResponse {
  marketing: { summary: MarketingSummary; daily: MarketingDay[]; spendByChannel: { channel: string; spend: number }[] };
  channels: {
    channel: string;
    spend: number;
    attributedRevenue: number;
    conversions: number;
    roas: number | null;
    cpa: number | null;
    share: number;
  }[];
  attribution: { model: string; windowDays: number; journeysAnalyzed: number; journeysSkipped: number; thinSample: boolean };
  mix: MixResult;
  range: { start: string; end: string };
}

export interface RevenueWebsite {
  id: string;
  domain: string;
  trackingId: string;
  createdAt: string;
}

export interface RevenueMetrics {
  grossRevenue: number;
  netRevenue: number;
  mrr: number;
  arr: number;
  ltv: number;
  cac: number;
  roas: number | null;
  conversionRate: number;
  revenuePerVisitor: number;
  newCustomerRevenue: number;
  returningCustomerRevenue: number;
  averageOrderValue: number;
}

export interface ChannelBreakdown {
  channel: string;
  visitors: number;
  conversions: number;
  revenue: number;
  spend: number;
  revenuePerVisitor: number;
  conversionRate: number;
  attributionShare: number;
}

export interface RevenueCustomer {
  id: string;
  email: string;
  name: string | null;
  createdAt: string;
}

export type AttributionModelName =
  | 'first_touch'
  | 'last_touch'
  | 'linear'
  | 'time_decay'
  | 'position_based'
  | 'markov'
  | 'shapley';

export interface ChannelAttribution {
  channel: string;
  creditShare: number;
  revenue: number;
  conversions: number;
}

export interface ModelAttribution {
  model: AttributionModelName;
  conversions: number;
  attributedRevenue: number;
  channels: ChannelAttribution[];
}

export interface AttributionComparisonData {
  models: ModelAttribution[];
  journeysAnalyzed: number;
  journeysSkipped: number;
  thinSample: boolean;
}

export interface CampaignSpendEntry {
  id: string;
  campaign: string;
  provider: string;
  channel: string | null;
  amount: number;
  currency: string;
  periodStart: string;
  periodEnd: string;
}

// --- dataset → revenue import ---

export type RevenueImportMode = 'transactions' | 'spend';

/** Target field → dataset column name; null means "not mapped". */
export interface RevenueImportMapping {
  date: string | null;
  amount: string | null;
  customerEmail: string | null;
  customerName: string | null;
  channel: string | null;
  campaign: string | null;
  spend: string | null;
  endDate: string | null;
}

export interface RevenueImportColumn {
  name: string;
  type: string;
  sample: string[];
}

export interface RevenueImportResult {
  imported: number;
  skipped: number;
  errors: string[];
}

export interface CustomerJourneyData {
  customerId: string;
  visitorId: string;
  touchpoints: Array<{
    id: string;
    channel: string;
    source: string | null;
    medium: string | null;
    campaign: string | null;
    landingPage: string;
    occurredAt: string;
  }>;
  totalRevenue: number;
  firstTouchAt: string;
  lastTouchAt: string;
  conversionAt: string | null;
}

export type FunnelStepInput =
  | { kind: 'event'; name: string }
  | { kind: 'url'; path: string };

export interface FunnelStepResultData {
  step: FunnelStepInput;
  visitors: number;
  conversionFromPrevious: number | null;
  overallConversion: number | null;
  medianTimeFromPreviousMs: number | null;
}

export interface FunnelResultData {
  steps: FunnelStepResultData[];
  visitorsInWindow: number;
  eventsConsidered: number;
}

export type RetentionGrainName = 'week' | 'month';
export type RetentionEntityName = 'customers' | 'visitors';

export interface RetentionCellData {
  active: number;
  rate: number;
}

export interface RetentionCohortData {
  key: string;
  start: string;
  size: number;
  cells: (RetentionCellData | null)[];
}

export interface RetentionGridData {
  entity: RetentionEntityName;
  grain: RetentionGrainName;
  periodKeys: string[];
  cohorts: RetentionCohortData[];
}


